import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { BookOpen, Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { ProductCard } from "@/features/sales/storefront/components/ProductCard";
import { PageIntro } from "@/features/sales/storefront/components/PageIntro";
import {
  categoriesWithProducts,
  getProductCategories,
} from "@/features/sales/storefront/catalog/data";
import { searchCatalog, useCatalog } from "@/features/sales/storefront/catalog/CatalogContext";
import type { Level } from "@/features/sales/storefront/catalog/types";
import { absoluteUrl } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const searchSchema = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  level: z.string().optional(),
  sort: z.enum(["relevance", "price-asc", "price-desc", "newest"]).optional(),
});

export const Route = createFileRoute("/productos/catalogo")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Librería de inglés — Socrattica" },
      {
        name: "description",
        content: "Explora libros digitales de inglés por nivel, habilidad y categoría.",
      },
      { property: "og:title", content: "Librería de inglés — Socrattica" },
      {
        property: "og:description",
        content: "Catálogo digital de libros y guías para aprender inglés.",
      },
      { property: "og:url", content: absoluteUrl("/productos/catalogo") },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/productos/catalogo") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Librería de inglés de Socrattica",
          description: "Libros digitales y guías prácticas para aprender inglés.",
          url: absoluteUrl("/productos/catalogo"),
          isPartOf: {
            "@type": "WebSite",
            name: "Socrattica",
            url: absoluteUrl("/"),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Inicio",
              item: absoluteUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Biblioteca",
              item: absoluteUrl("/productos/catalogo"),
            },
          ],
        }),
      },
    ],
  }),
  component: CatalogPage,
});

const LEVELS: Level[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

function CatalogPage() {
  const { products, isLoading, isLoadingMore, hasMore, error, refresh, loadMore } = useCatalog();
  const search = useSearch({ from: "/productos/catalogo" });
  const [q, setQ] = useState(search.q ?? "");
  const [category, setCategory] = useState(search.category ?? "all");
  const [level, setLevel] = useState(search.level ?? "all");
  const [sort, setSort] = useState(search.sort ?? "relevance");
  const [searchProducts, setSearchProducts] = useState<
    Awaited<ReturnType<typeof searchCatalog>>["products"] | null
  >(null);
  const [searchCursor, setSearchCursor] = useState<string>();
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  const displayedProducts = searchProducts ?? products;
  const activeCategories = categoriesWithProducts(displayedProducts);

  const filtered = useMemo(() => {
    let list = displayedProducts.slice();
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) =>
        (p.title + " " + (p.subtitle ?? "") + " " + p.description).toLowerCase().includes(s),
      );
    }
    if (category !== "all") {
      list = list.filter((p) => getProductCategories(p).includes(category));
    }
    if (level !== "all") list = list.filter((p) => p.level === level);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "newest")
      list.sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""));
    else
      list.sort(
        (a, b) =>
          Number(Boolean(b.featured)) - Number(Boolean(a.featured)) ||
          (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""),
      );
    return list;
  }, [displayedProducts, q, category, level, sort]);

  const clearAll = () => {
    setQ("");
    setCategory("all");
    setLevel("all");
    setSort("relevance");
  };
  const hasFilters = q || category !== "all" || level !== "all" || sort !== "relevance";
  const catalogIsEmpty = displayedProducts.length === 0;

  useEffect(() => {
    const normalizedQuery = q.trim();
    if (normalizedQuery.length < 2) {
      setSearchProducts(null);
      setSearchCursor(undefined);
      setSearchHasMore(false);
      setSearchError("");
      setSearchLoading(false);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setSearchLoading(true);
      setSearchError("");
      void searchCatalog(normalizedQuery)
        .then((page) => {
          if (!active) return;
          setSearchProducts(page.products);
          setSearchCursor(page.nextCursor);
          setSearchHasMore(page.hasMore);
        })
        .catch((requestError: unknown) => {
          if (!active) return;
          setSearchError(
            requestError instanceof Error ? requestError.message : "No pudimos buscar productos.",
          );
        })
        .finally(() => {
          if (active) setSearchLoading(false);
        });
    }, 350);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [q]);

  const loadMoreSearch = () => {
    if (!searchCursor || !searchHasMore || searchLoading) return;
    setSearchLoading(true);
    void searchCatalog(q.trim(), searchCursor)
      .then((page) => {
        setSearchProducts((current) => {
          const merged = new Map((current ?? []).map((product) => [product.id, product]));
          for (const product of page.products) merged.set(product.id, product);
          return Array.from(merged.values());
        });
        setSearchCursor(page.nextCursor);
        setSearchHasMore(page.hasMore);
      })
      .catch((requestError: unknown) => {
        setSearchError(
          requestError instanceof Error ? requestError.message : "No pudimos buscar productos.",
        );
      })
      .finally(() => setSearchLoading(false));
  };

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) return;
    const timer = window.setTimeout(() => {
      void trackEvent("search", {
        search_term: query,
        results_count: filtered.length,
      });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [filtered.length, q]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-black">
      <SiteHeader />
      <main id="main-content">
        <PageIntro
          compact
          eyebrow="Tienda digital"
          title="Recursos para avanzar"
          description="Guías claras, prácticas y listas para descargar."
          details={["Por nivel", "Práctico", "Descargable"]}
        />

        <section id="categorias" className="border-b border-black/10 bg-white py-5">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="rounded-xl border border-black/15 bg-white p-4">
              <div className="mb-4 flex items-center gap-3 border-b border-black/10 pb-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-black text-white">
                  <SlidersHorizontal className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="text-sm font-semibold">Filtrar recursos</h2>
                  <p className="mt-0.5 text-xs text-black/65">Encuentra lo que necesitas.</p>
                </div>
              </div>

              <div className="grid items-end gap-4 md:grid-cols-[minmax(220px,1fr)_auto_auto_auto]">
                <div>
                  <label
                    htmlFor="q"
                    className="mb-2 block text-xs font-medium uppercase tracking-widest text-black/65"
                  >
                    Buscar
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-black/60" />
                    <Input
                      id="q"
                      placeholder="Título o palabra clave"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="h-11 rounded-lg border-black/20 bg-white pl-11"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="catalog-category"
                    className="mb-2 block text-xs font-medium uppercase tracking-widest text-black/65"
                  >
                    Categoría
                  </label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger
                      id="catalog-category"
                      className="h-11 w-full rounded-lg border-black/20 bg-white px-4 text-black shadow-none md:w-[180px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-black bg-white p-1 text-black">
                      <SelectItem value="all">Todas</SelectItem>
                      {activeCategories.map((c) => (
                        <SelectItem key={c.slug} value={c.slug}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor="catalog-level"
                    className="mb-2 block text-xs font-medium uppercase tracking-widest text-black/65"
                  >
                    Nivel
                  </label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger
                      id="catalog-level"
                      className="h-11 w-full rounded-lg border-black/20 bg-white px-4 text-black shadow-none md:w-[140px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-black bg-white p-1 text-black">
                      <SelectItem value="all">Todos</SelectItem>
                      {LEVELS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor="catalog-sort"
                    className="mb-2 block text-xs font-medium uppercase tracking-widest text-black/65"
                  >
                    Orden
                  </label>
                  <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
                    <SelectTrigger
                      id="catalog-sort"
                      className="h-11 w-full rounded-lg border-black/20 bg-white px-4 text-black shadow-none md:w-[180px]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-black bg-white p-1 text-black">
                      <SelectItem value="relevance">Relevancia</SelectItem>
                      <SelectItem value="price-asc">Precio: menor a mayor</SelectItem>
                      <SelectItem value="price-desc">Precio: mayor a menor</SelectItem>
                      <SelectItem value="newest">Más nuevos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {hasFilters && (
                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-black/5 pt-5">
                  <span className="text-sm font-medium text-black/70">Filtros activos</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAll}
                    className="rounded-lg text-black hover:bg-black hover:text-white"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Limpiar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white py-5 sm:py-3">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            {isLoading || (searchLoading && searchProducts === null) ? (
              <div className="rounded-xl border border-black/15 bg-white px-6 py-20 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-black/15 border-t-black" />
                <p className="mt-5 text-sm text-black/65">Cargando la librería…</p>
              </div>
            ) : error || searchError ? (
              <div className="rounded-xl border border-black bg-white px-6 py-16 text-center">
                <h2 className="text-lg font-semibold">No pudimos cargar la librería</h2>
                <p className="mt-2 text-xs text-black/70">{searchError || error}</p>
                <Button className="mt-6" onClick={refresh}>
                  Reintentar
                </Button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-xl border border-black/15 bg-white px-6 py-16 text-center sm:py-20">
                <div className="mx-auto flex max-w-xl flex-col items-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white">
                    {catalogIsEmpty ? (
                      <BookOpen className="h-7 w-7" />
                    ) : (
                      <Search className="h-7 w-7" />
                    )}
                  </span>
                  <div className="mt-5 text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">
                    {catalogIsEmpty ? "Catálogo en preparación" : "Sin resultados"}
                  </div>
                  <p className="mt-3 max-w-md text-xs leading-5 text-black/70">
                    {catalogIsEmpty
                      ? "Los libros aparecerán aquí cuando se publiquen desde el catálogo."
                      : "Prueba con otros filtros o busca por palabra clave."}
                  </p>
                  {!catalogIsEmpty && (
                    <Button className="mt-6" onClick={clearAll}>
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {filtered.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
                {(searchProducts ? searchHasMore : hasMore) && (
                  <div className="mt-8 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={searchProducts ? searchLoading : isLoadingMore}
                      onClick={searchProducts ? loadMoreSearch : loadMore}
                    >
                      {searchProducts
                        ? searchLoading
                          ? "Cargando…"
                          : "Cargar más resultados"
                        : isLoadingMore
                          ? "Cargando…"
                          : "Cargar más recursos"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
